import { css, type Theme } from '@emotion/react';
import { Warning } from '@phosphor-icons/react';
import { CoreLimitDialog } from 'client/pages/deployment/components/CoreLimitDialog';
import { NoCreditsDialog } from 'client/pages/deployment/components/NoCreditsDialog';
import { getDeploymentStageIcon } from 'client/ClientConstants';
import { ToggleButton, type ToggleButtonProps } from 'client/components/Button';
import { DropdownMenu } from 'client/components/DropdownMenu';
import { useDeploymentEnabledState } from '../useDeploymentEnabledState';

export interface EnabledToggleButtonProps extends Partial<ToggleButtonProps> {
  deploymentID: number | null;
  stageIdentifier: string;
}

export const EnabledToggleButton = ({ deploymentID, stageIdentifier, ...other }: EnabledToggleButtonProps) => {
  const {
    enabled,
    isToggleable,
    startOptions,
    toggle,
    isCoreLimitDialogOpen,
    setIsCoreLimitDialogOpen,
    isNoCreditsDialogOpen,
    setIsNoCreditsDialogOpen,
  } = useDeploymentEnabledState({
    deploymentID,
  });
  const stageIcon = getDeploymentStageIcon(stageIdentifier);

  const handleDisable = () => {
    toggle(null);
  };

  const confirmMenuItems = [
    <DropdownMenu.Banner
      key="message"
      title={`Disable a ${stageIdentifier} deployment?`}
      subtitle={`This is a ${stageIdentifier} environment. Are you sure you want to disable it?`}
      icon={<stageIcon.type {...stageIcon.props} animate />}
      ornament={<Warning />}
      state="warning"
    />,
    <DropdownMenu.Button key="disable" id="disable" danger onSelect={handleDisable} css={disableButtonStyle}>
      Disable Deployment
    </DropdownMenu.Button>,
  ];

  const handleToggle = (duration: string | null) => {
    if (!enabled) return;
    if (enabled && stageIdentifier !== 'development') return;
    toggle(duration);
  };

  const button = (
    <ToggleButton checked={enabled} onChange={() => handleToggle(null)} disabled={!deploymentID || !isToggleable} {...other}>
      {enabled ? 'Enabled' : 'Disabled'}
    </ToggleButton>
  );

  if (enabled && stageIdentifier !== 'development') {
    return <DropdownMenu items={confirmMenuItems}>{button}</DropdownMenu>;
  }

  if (!enabled) {
    return (
      <>
        <DropdownMenu items={startOptions} onSelect={toggle}>
          {button}
        </DropdownMenu>
        <CoreLimitDialog open={isCoreLimitDialogOpen} onClose={() => setIsCoreLimitDialogOpen(false)} />
        <NoCreditsDialog open={isNoCreditsDialogOpen} onClose={() => setIsNoCreditsDialogOpen(false)} />
      </>
    );
  }

  return <>{button}</>;
};

const disableButtonStyle = (theme: Theme) => css`
  background-color: ${theme.color.red[100]};
  color: ${theme.color.white};

  .icon {
    color: ${theme.color.white};
  }
`;
